"use client";

import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import Loading from "@/app/loading";
import RCRange from "@/components/form/rc-slider-range";
import FetchError from "@/components/ui/fetch-error";

import QuestionnaireTable from "./questionnaire-table";

import api from "@/lib/api";

type FormValues = {
  years?: number[];
};

type SurveyQuestionnaireProps = {
  survey_types: {
    value: number;
    label: string;
  }[];
  years: number[];
};

type Survey = {
  id: number | null;
  survey_type: number;
  study: number;
  survey: number | null;
  module: number | null;
  title: string;
  year: number;
  studyName: string;
};

type SurveyQuestionnaireDataProps = {
  years: string[];
  data: {
    [key: string]: {
      id: number;
      has_overview: boolean;
      years: {
        [year: string]: Survey[];
      };
    };
  };
};

export async function getQuestionnaireData(survey_type: number, yearStart: number, yearEnd: number) {
  try {
    const params = new URLSearchParams({
      survey_type: survey_type.toString(),
      years: `${yearStart},${yearEnd}`,
    });

    const response = await api.get<SurveyQuestionnaireDataProps>(`/srv/questionnaire/data?${params.toString()}`);
    return response;
  } catch (error) {
    console.error("Error fetching questionnaires data:", error);
    throw new Error("Failed to fetch questionnaires data");
  }
}

export default function QuestionnaireHome() {
  const { watch, reset, setValue } = useForm<FormValues>({
    defaultValues: {
      years: [],
    },
  });

  const yearsValue = watch("years");

  const DEBOUNCE_DELAY = 300; // milliseconds

  const [tableData, setTableData] = useState<SurveyQuestionnaireDataProps>();
  const [yearSelection, setYearSelection] = useState<number[]>();
  const [hcapSurveyType, setHcapSurveyType] = useState<number | null>(null);

  const { data, error, isPending } = useQuery({
    queryKey: ["srv-questionnaire"],
    queryFn: () => api.get<SurveyQuestionnaireProps>("/srv/questionnaire/home/?survey_type=6"),
  });

  // Initialize year selection and HCAP survey type when data is fetched
  useEffect(() => {
    if (data) {
      setYearSelection(data.years);
      setValue("years", data.years);

      // Find HCAP survey type value
      const hcap = data.survey_types.find((type) => type.label === "HCAP");
      setHcapSurveyType(hcap ? hcap.value : null);
    }
  }, [data, setValue]);

  // Fetch questionnaire data when year selection or HCAP survey type changes, with debounce
  useEffect(() => {
    const handler = setTimeout(() => {
      const fetchData = async () => {
        if (hcapSurveyType !== null && yearSelection) {
          const response = await getQuestionnaireData(hcapSurveyType, yearSelection[0], yearSelection[1]);
          setTableData(response);
        }
      };
      fetchData();
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(handler);
  }, [hcapSurveyType, yearSelection]);

  if (isPending) return <Loading />;
  if (error) return <FetchError message={error.message} />;
  if (yearsValue?.length === 0) return <Loading />;

  const handleReset = () => {
    if (!data) return;
    setYearSelection(data.years);
    reset({
      years: data.years,
    });
  };

  return (
    <>
      <div className="alert alert-primary mt-4">Many components are not searchable due to copyright issues.</div>
      <form className="row g-3 mb-4">
        <div className="col-md-4 col-sm-6 mt-2">
          <RCRange
            label={"Year"}
            min={data?.years[0]}
            max={data?.years[1]}
            value={Array.isArray(yearSelection) ? yearSelection : []}
            onChange={(value) => setYearSelection(Array.isArray(value) ? value : [value])}
          />
        </div>

        <div className="col d-flex justify-content-end align-items-end">
          <button
            type="button"
            className="btn btn-outline-secondary"
            id="reset-btn"
            title="Clear All"
            onClick={() => handleReset()}
          >
            <i className="bi bi-arrow-repeat"></i>
          </button>
        </div>
      </form>
      {tableData !== undefined && <QuestionnaireTable headers={["STUDY", ...tableData.years]} data={tableData.data} />}
    </>
  );
}
